import { ApiResponse } from '@interfaces/index';
import { useQuery } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

export const indicatorsApiHandler = {
  // GET /get-user-latest-indicators
  getLatestIndicators: async (): Promise<ApiResponse<IndicatorsResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get('/get-user-latest-indicators'),

  // GET localhost:8001/v2/user-indicator-history/2?startDate='2025-06-16'&endDate='2025-06-16'
  getIndicatorHistoryDetailsById: async (
    indicatorId: number,
  ): Promise<ApiResponse<IndicatorHistoryResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(`/user-indicator-history/${indicatorId}`),

  // GET/indicator-assessment-details/2
  getIndicatorAssessmentDetailsById: async (
    indicatorId: number,
  ): Promise<ApiResponse<QuestionnaireIndicator>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/indicator-assessment-details/${indicatorId}`,
    ),

  // GET/get-indicator-key-insights/2
  getIndicatorKeyInsight: async (indicatorId: number): Promise<ApiResponse<IndicatorInsight>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-indicator-key-insights/${indicatorId}`,
    ),
};

export const useIndicatorsHooks = {
  // React Query: GET /get-user-latest-indicators
  useLatestIndicators: () =>
    useQuery({
      queryKey: ['user-latest-indicators'],
      queryFn: indicatorsApiHandler.getLatestIndicators,
      // staleTime: 2 * 60 * 1000, // optional: 2 minutes
    }),

  // React Query: GET /user-indicator-history/:indicatorId
  useIndicatorHistoryDetails: (indicatorId: number | null) =>
    useQuery({
      queryKey: ['indicator-history-details', indicatorId],
      queryFn: () => indicatorsApiHandler.getIndicatorHistoryDetailsById(indicatorId || 0),
      enabled: !!indicatorId,
    }),

  // React Query: GET /user-indicator-history/:indicatorId
  useIndicatorAssessmentDetails: (indicatorId: number | null) =>
    useQuery({
      queryKey: ['indicator-assessment-details', indicatorId],
      queryFn: () => indicatorsApiHandler.getIndicatorAssessmentDetailsById(indicatorId || 0),
      enabled: !!indicatorId,
    }),

  useIndicatorKeyInsight: (indicatorId: number | null) =>
    useQuery({
      queryKey: ['indicator-key-insight', indicatorId],
      queryFn: () => indicatorsApiHandler.getIndicatorKeyInsight(indicatorId || 0),
      enabled: !!indicatorId,
    }),
};

export interface IndicatorsResponse {
  id: number;
  name: string;
  description: string;
  isPositive: boolean;
  status: IndicatorStatusEnum;
  lowThreshold: number;
  highThreshold: number;
  displayOrder: number;
  iconName: string;
  confidenceLevel: number;
  dataFreshness: DataFreshnessEnum;
  lastUpdated: string;
  hasSpecialisedAssessment: boolean;
  specialisedQuestionnaireId: number | null;
  scoreSourceText?: string;
  insightText?: string;
  latestIndicatorScore: LatestIndicatorScore;
}

export interface LatestIndicatorScore {
  id: number;
  userId: number;
  indicatorId: number;
  score: number; // -1 indicates no score in database, otherwise 0-100
  scoreSourceText: string;
  insightText: string;
  confidenceLevel: string;
  alphaWeight: string;
  dataFreshness: DataFreshnessEnum;
  isCurrent: boolean;
  primarySourceType: string;
  primarySourceId: number;
  explanation: string;
  formula: string;
  createdAt: string;
  updatedAt: string;
}

export enum IndicatorStatusEnum {
  Low = 'low',
  Moderate = 'moderate',
  High = 'high',
}

export enum DataFreshnessEnum {
  Current = 'current',
  Aging = 'aging',
  Outdated = 'outdated',
}

export interface Indicator {
  id: number;
  name: string;
  description: string;
  isPositive: boolean;
  lowThreshold: number;
  highThreshold: number;
  displayOrder: number;
  iconName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DetectedIndicator {
  indicator: string;
  score: number;
  confidence: number;
  evidence: string;
  keywords: string[];
}

export interface Explanation {
  detectedIndicators: DetectedIndicator[];
  undetectedIndicators: string[];
}

export interface HistoryItem {
  id: number;
  userId: number;
  indicatorId: number;
  score: number;
  scoreSourceText: string;
  insightText: string;
  confidenceLevel: string;
  alphaWeight: string;
  primarySourceType: string;
  primarySourceId: number;
  explanation: string; // JSON string of Explanation
  formula: string;
  createdAt: string;
  updatedAt: string;
  indicator: Indicator;
  status: IndicatorStatusEnum;
}

export interface DateRange {
  from: string;
  to: string;
}

export interface HistorySummary {
  totalDataPoints: number;
  dateRange: DateRange;
  trend: string;
  scoreChange: number;
  percentageChange: number;
  averageScore: number;
  lowestScore: number;
  highestScore: number;
}

export interface IndicatorHistoryResponse {
  history: HistoryItem[];
  historySummery: HistorySummary;
}

export interface QuestionnaireIndicator {
  id: number;
  questionnaireId: number;
  indicatorId: number;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
  questionnaire: {
    id: number;
    name: string;
    shortDescription: string;
    description: string;
    version: string;
    type: string | null;
    default: boolean;
    createdAt: string;
    updatedAt: string;
    scoringType: string;
    scoreCalculationType: string;
    outputType: string;
    factorSetId: number | null;
    questionnaireType: string;
    color: string | null;
    icon: string | null;
    ewsTriggeredBy: string;
    maxScore: number;
    minScore: number;
  };
  indicator: Indicator;
}

export interface IndicatorInsight {
  id: number;
  userId: number;
  indicatorId: number;
  insight: string[];
  trigger: {
    indicatorId: number;
    indicator: string;
  };
  lastScore: number;
  createdAt: string; // ISO 8601 date string
  updatedAt: string; // ISO 8601 date string
  recommendedActions: string[] | null;
}
